import { useQuery } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { ChatThread } from "types/ChatThread";
import { getThreads } from "../methods/getThreads";

interface UseThreadsQueryOptions<T> {
  select?: (threads: ChatThread[]) => T;
}

/**
 * Fetches metadata for all of the caller's chat threads (used by the sidebar).
 *
 * React Query's automatic focus invalidation is disabled globally (the query
 * client sets `staleTime: Infinity`) so that streaming code paths can mutate
 * the cache without RQ clobbering them. Routes that display threads must
 * therefore call `refetch()` from a manual `window` focus listener, gated on
 * any in-flight streams. See `Chat.tsx`, `NewChat.tsx`, and
 * `IncognitoChat.tsx` for reference implementations.
 */
export default function useThreadsQuery<T = ChatThread[]>(
  opts?: UseThreadsQueryOptions<T>
) {
  const getAccessToken = useGetAccessToken();

  const query = useQuery({
    queryKey: ["cc-db-api", "threads"],
    queryFn: () => getThreads(getAccessToken),
    retry: false,
    select: opts?.select,
  });

  return query;
}
